import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Type, Image as ImageIcon, ArrowRight, Minus, Hash, Calendar, CheckSquare, Sliders, ChevronRight, GripVertical
} from 'lucide-react';

const sectionMotion = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
};

// Draggable element rows inside a section card (FECOA-2174)
const itemClass =
  'group/row flex w-full cursor-grab select-none items-center gap-2.5 px-4 py-2.5 text-[13.5px] text-gray-700 transition-colors hover:bg-gray-50/70 active:cursor-grabbing dark:text-gray-300 dark:hover:bg-white/[0.03]';

// Muted grip on the right of each row, so the rows read as draggable
const DragGrip = () => (
  <GripVertical aria-hidden="true" className="ml-auto h-4 w-4 flex-shrink-0 text-gray-300 transition-colors group-hover/row:text-gray-400 dark:text-gray-600 dark:group-hover/row:text-gray-500" />
);

// Transparent 1×1 PNG used to suppress the browser's native HTML5 drag ghost.
// Without this, the browser renders a faded snapshot of the entire sidebar
// row, which duplicates (and overlaps) our in-canvas drop preview.
const EMPTY_DRAG_IMAGE = typeof Image !== 'undefined' ? (() => {
  const img = new Image();
  img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  return img;
})() : null;

const ElementsPanel = ({
  setDraggingFromSidebar,
  setDragPreviewPosition,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    basic: false,
    input: false,
  });

  const handleDragStart = (e, type) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify({ type }));
    if (EMPTY_DRAG_IMAGE) {
      e.dataTransfer.setDragImage(EMPTY_DRAG_IMAGE, 0, 0);
    }
    setDraggingFromSidebar({ type });
  };

  const handleDragEnd = () => {
    setDraggingFromSidebar(null);
    setDragPreviewPosition(null);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <>
      {/* Basic Elements */}
      <div className="card overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('basic')}
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors hover:bg-gray-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/60 dark:hover:bg-white/[0.03]"
          aria-expanded={expandedSections.basic}
          aria-controls="elements-panel-basic"
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Type className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h3 className="truncate text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Basic Elements</h3>
              <p className="badge badge-gray flex-shrink-0 tabular-nums">4 elements</p>
            </div>
          </div>
          <motion.span
            animate={{ rotate: expandedSections.basic ? 90 : 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="inline-flex"
          >
            <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          </motion.span>
        </button>
        <AnimatePresence initial={false}>
          {expandedSections.basic && (
            <motion.div
              id="elements-panel-basic"
              key="basic"
              {...sectionMotion}
              className="overflow-hidden"
            >
              <div className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-white/[0.06] dark:border-white/[0.06]">
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'text')}
                  onDragEnd={handleDragEnd}
                  className={itemClass}
                >
                  <Type className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <span className="font-medium">Text</span>
                  <DragGrip />
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'image')}
                  onDragEnd={handleDragEnd}
                  className={itemClass}
                >
                  <ImageIcon className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <span className="font-medium">Image</span>
                  <DragGrip />
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'button')}
                  onDragEnd={handleDragEnd}
                  className={itemClass}
                >
                  <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <span className="font-medium">Button (Next/Back)</span>
                  <DragGrip />
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'divider')}
                  onDragEnd={handleDragEnd}
                  className={itemClass}
                >
                  <Minus className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <span className="font-medium">Divider</span>
                  <DragGrip />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Input Elements */}
      <div className="card overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('input')}
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors hover:bg-gray-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/60 dark:hover:bg-white/[0.03]"
          aria-expanded={expandedSections.input}
          aria-controls="elements-panel-input"
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Hash className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h3 className="truncate text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Input Fields</h3>
              <p className="badge badge-gray flex-shrink-0 tabular-nums">5 elements</p>
            </div>
          </div>
          <motion.span
            animate={{ rotate: expandedSections.input ? 90 : 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="inline-flex"
          >
            <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          </motion.span>
        </button>
        <AnimatePresence initial={false}>
          {expandedSections.input && (
            <motion.div
              id="elements-panel-input"
              key="input"
              {...sectionMotion}
              className="overflow-hidden"
            >
              <div className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-white/[0.06] dark:border-white/[0.06]">
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'text-input')}
                  onDragEnd={handleDragEnd}
                  className={itemClass}
                >
                  <Type className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <span className="font-medium">Text Input</span>
                  <DragGrip />
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'number-input')}
                  onDragEnd={handleDragEnd}
                  className={itemClass}
                >
                  <Hash className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <span className="font-medium">Number Input</span>
                  <DragGrip />
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'date-picker')}
                  onDragEnd={handleDragEnd}
                  className={itemClass}
                >
                  <Calendar className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <span className="font-medium">Date Picker</span>
                  <DragGrip />
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'checkbox')}
                  onDragEnd={handleDragEnd}
                  className={itemClass}
                >
                  <CheckSquare className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <span className="font-medium">Checkbox Group</span>
                  <DragGrip />
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'scale')}
                  onDragEnd={handleDragEnd}
                  className={itemClass}
                >
                  <Sliders className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <span className="font-medium">Scale / Slider</span>
                  <DragGrip />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default ElementsPanel;
